
/** @format */

import React, { type Ref } from 'react'
import { Image, Modal, Popover } from 'antd'
import type { CompoundFC } from '@/shared/lib/compound'

const cx = (...classes: any[]) => classes.filter(Boolean).join(' ')

const CustomMask = ({ width, height, className, style, children, ref, ...props }: any & { ref?: Ref<any> }) => (
  <div
    ref={ref}
    className={cx(
      'flex rounded absolute top-0 right-0 bottom-0 text-base text-white items-center justify-center z-10 cursor-pointer bg-black/60',
      className
    )}
    style={{
      width: width || '50px',
      height: height || '50px',
      ...style,
    }}
    {...props}
  >
    {children}
  </div>
)
CustomMask.displayName = 'CustomMask'

const Wrapper = ({ title, className, style, children, ref, ...props }: any & { ref?: Ref<any> }) => (
  <div
    ref={ref}
    className={cx('w-full rounded-[5px] bg-white', className)}
    style={{
      padding: title?.length ? '14px 0px' : 0,
      ...style,
    }}
    {...props}
  >
    {children}
  </div>
)
Wrapper.displayName = 'Wrapper'

const FileWrapper = ({ width, height, className, style, children, ref, ...props }: any & { ref?: Ref<any> }) => (
  <div
    ref={ref}
    className={cx('relative group', className)}
    style={{
      width: width || '50px',
      height: height || '50px',
      ...style,
    }}
    {...props}
  >
    {children}
  </div>
)
FileWrapper.displayName = 'FileWrapper'

const InputsBox = ({ maxCount, title, right, fontSize, width, height, className, style, children, ref, ...props }: any & { ref?: Ref<any> }) => (
    <div
      ref={ref}
      className={cx(
        'flex relative max-md:flex-col gap-y-3.5',
        className
      )}
      style={{
        width: maxCount ? '108px' : undefined,
        flexWrap: maxCount ? 'nowrap' : 'wrap',
        gridColumnGap: title?.length ? '30px' : '',
        overflow: maxCount ? 'hidden' : undefined,
        justifyContent: right ? 'flex-end' : 'flex-start',
        ...style,
      }}
      {...props}
    >
      {children}
    </div>
  )
InputsBox.displayName = 'InputsBox'

const ButtonBox = ({ className, style, children, ref, ...props }: any & { ref?: Ref<any> }) => (
  <div
    ref={ref}
    className={cx('w-[120px] justify-between flex absolute -top-[17px] right-[30px]', className)}
    style={style}
    {...props}
  >
    {children}
  </div>
)
ButtonBox.displayName = 'ButtonBox'

const PreviewModal = ({ className, style, children, ...props }: any & { ref?: Ref<any> }) => (
  <Modal
    className={className}
    style={{ width: '1000px', ...style }}
    {...props}
  >
    {children}
  </Modal>
)
PreviewModal.displayName = 'PreviewModal'

const CustomImage = ({ width, height, className, style, ref, ...props }: any & { ref?: Ref<any> }) => (
  <img
    ref={ref}
    className={cx('max-h-screen', className)}
    style={{
      width: width ? `${width}px` : undefined,
      height: height ? `${height}px` : undefined,
      ...style,
    }}
    {...props}
  />
)
CustomImage.displayName = 'CustomImage'

const Container = ({ className, style, children, ref, ...props }: any & { ref?: Ref<any> }) => (
  <div
    ref={ref}
    className={cx('flex gap-5 p-2.5', className)}
    style={style}
    {...props}
  >
    {children}
  </div>
)
Container.displayName = 'Container'

const Card: CompoundFC = ({ trmoves, imgHeight, className, style, children, ref, ...props }: any & { ref?: Ref<any> }) => (
  <div
    ref={ref}
    className={cx('relative max-w-[396px]', className)}
    style={{
      display: !trmoves ? 'flex' : 'grid',
      gridTemplateColumns: !trmoves ? '' : '1fr 1fr',
      gridColumnGap: !trmoves ? '4px' : '30px',
      height: imgHeight || '170px',
      marginTop: !trmoves ? 0 : '15px',
      ...style,
    }}
    {...props}
  >
    {children}
  </div>
)
Card.displayName = 'Card'

const CardImage = ({ imgHeight, imgWidth, className, style, children, ref, ...props }: any & { ref?: Ref<any> }) => (
  <div
    ref={ref}
    className={cx('rounded overflow-hidden', className)}
    style={{
      height: imgHeight || '170px',
      width: imgWidth || '120px',
      ...style,
    }}
    {...props}
  >
    {children}
  </div>
)
CardImage.displayName = 'CardImage'
Card.Image = CardImage

const CardContent = ({ imgHeight, className, style, children, ref, ...props }: any & { ref?: Ref<any> }) => (
  <div
    ref={ref}
    className={cx('flex flex-col overflow-y-auto overflow-x-hidden', className)}
    style={{
      height: imgHeight || '170px',
      ...style,
    }}
    {...props}
  >
    {children}
  </div>
)
CardContent.displayName = 'CardContent'
Card.Content = CardContent

const CurrentInfo = ({ className, ref, ...props }: any & { ref?: Ref<any> }) => (
  <Popover ref={ref} className={className} {...props} />
)
CurrentInfo.displayName = 'CurrentInfo'

const Img = ({ imgHeight, imgWidth, className, style, ...props }: any & { ref?: Ref<any> }) => (
  <Image
    className={cx('relative rounded overflow-hidden object-fill', className)}
    style={{
      height: imgHeight || '170px',
      width: imgWidth || '120px',
      ...style,
    }}
    {...props}
  />
)
Img.displayName = 'Img'

const AntImage = ({ imgWidth, imgHeight, className, style, ...props }: any & { ref?: Ref<any> }) => (
  <Image
    className={cx('object-cover cursor-pointer hover:!opacity-100', className)}
    style={{
      width: imgWidth || '35px',
      height: imgHeight || '35px',
      ...style,
    }}
    {...props}
  />
)
AntImage.displayName = 'AntImage'

const DownloadingSpinner = ({ className, style, ref, ...props }: any & { ref?: Ref<any> }) => (
  <img
    ref={ref}
    className={cx('w-[50px] h-[50px]', className)}
    style={style}
    {...props}
  />
)
DownloadingSpinner.displayName = 'DownloadingSpinner'

const InfoStyle = ({ className, style, children, ref, ...props }: any & { ref?: Ref<any> }) => (
  <div
    ref={ref}
    className={cx('flex gap-[5px] bg-white w-fit max-w-[600px] flex-wrap h-fit relative', className)}
    style={style}
    {...props}
  >
    {children}
  </div>
)
InfoStyle.displayName = 'InfoStyle'

export {
  InfoStyle,
  Wrapper,
  InputsBox,
  ButtonBox,
  PreviewModal,
  CustomImage,
  Container,
  Card,
  CardImage,
  CardContent,
  Img,
  CustomMask,
  AntImage,
  CurrentInfo,
  FileWrapper,
  DownloadingSpinner,
}
